import { useCallback, useEffect, useRef, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AddProgressForm } from "@/components/admin/AddProgressForm";
import { StatusBadge } from "@/components/admin/JenisTag";
import { MediaThumbs } from "@/components/admin/MediaThumbs";
import { ProgressTimeline } from "@/components/progress/ProgressTimeline";
import { api, formatApiError } from "@/lib/api";
import { formatDateID, formatPhone } from "@/lib/format";

const Item = ({ label, children, wide }) => (
  <div className={wide ? "col-span-2" : undefined}>
    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</dt>
    <dd className="mt-1 break-words text-sm font-medium text-slate-900">{children}</dd>
  </div>
);

const InfoGrid = ({ complaint: c, owner }) => (
  <dl className="grid grid-cols-2 gap-4 rounded-2xl bg-slate-50 p-4">
    <Item label="Nama Pelapor">{c.nama}</Item>
    <Item label="Status"><StatusBadge status={c.status} /></Item>
    <Item label="Nomor HP">{formatPhone(c.nomor_hp) || "-"}</Item>
    <Item label="Akun Warga">{owner ? `${owner.username} · ${owner.email}` : "-"}</Item>
    <Item label="Alamat" wide>{c.alamat}</Item>
    <Item label="Dikirim" wide>{formatDateID(c.created_at, "d MMMM yyyy, HH:mm")}</Item>
  </dl>
);

export const ComplaintDetailDialog = ({ complaintId, onClose, onOpenMedia, onComplaintChange, onProgressAdded }) => {
  const [data, setData] = useState(null);
  const [notifyStatus, setNotifyStatus] = useState(null);
  const currentId = useRef(complaintId);
  currentId.current = complaintId;

  const load = useCallback(async (id) => {
    try {
      const { data: res } = await api.get(`/admin/complaints/${id}`);
      if (currentId.current !== id) return;
      setData(res);
      onComplaintChange(res.complaint);
    } catch (err) {
      toast.error(formatApiError(err, "Gagal memuat detail pengaduan"));
    }
  }, [onComplaintChange]);

  useEffect(() => {
    setData(null);
    if (!complaintId) return;
    load(complaintId);
    api.get("/admin/notify/status").then((res) => setNotifyStatus(res.data)).catch(() => setNotifyStatus(null));
  }, [complaintId, load]);

  const handleAdded = (res) => {
    setData((d) => ({ ...d, complaint: res.complaint, updates: [...d.updates, res.update] }));
    onProgressAdded(res.complaint);
    setTimeout(() => currentId.current === res.complaint.id && load(res.complaint.id), 5000);
  };

  const c = data?.complaint;
  return (
    <Dialog open={!!complaintId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-3xl p-0" data-testid="detail-dialog">
        {!c ? (
          <div className="flex items-center justify-center gap-2 p-16 text-sm text-slate-500">
            <DialogTitle className="sr-only">Memuat detail pengaduan</DialogTitle>
            <LoaderCircle className="h-5 w-5 animate-spin text-emerald-600" /> Memuat detail...
          </div>
        ) : (
          <div className="space-y-6 p-5 sm:p-8">
            <DialogHeader className="text-left">
              <p className="font-mono text-xs font-semibold text-emerald-700" data-testid="detail-ticket">#{c.ticket}</p>
              <DialogTitle className="font-heading text-xl font-bold">{c.jenis}</DialogTitle>
              <DialogDescription>Tanggal pengaduan: {formatDateID(c.tanggal, "EEEE, d MMMM yyyy")}</DialogDescription>
            </DialogHeader>
            <InfoGrid complaint={c} owner={data.owner} />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Detail Pengaduan</p>
              <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700" data-testid="detail-dialog-text">{c.detail}</p>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Lampiran</p>
              <MediaThumbs attachments={c.attachments} onOpen={onOpenMedia} ownerId={`detail-${c.id}`} />
            </div>
            <section className="space-y-4">
              <h4 className="font-heading text-base font-bold text-slate-900">Riwayat Progres & Tanggapan</h4>
              <ProgressTimeline updates={data.updates} viewer="admin" />
            </section>
            <AddProgressForm complaint={c} notifyStatus={notifyStatus} onAdded={handleAdded} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
